// ===========================================
// ActivityFeed Component
// ===========================================
// Displays recent school activity (enrollments, payments, bookings)

import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Card,
  CardContent,
  CardHeader,
  List,
  ListItem,
  ListItemAvatar,
  ListItemText,
  Avatar,
  Typography,
  Box,
  Button,
  Skeleton,
  Divider,
} from '@mui/material';
import { useTenant } from '../../contexts/TenantContext';
import {
  PersonAdd as EnrollmentIcon,
  Payment as PaymentIcon,
  EventAvailable as BookingIcon,
  CheckCircle as AttendanceIcon,
  CloudUpload as FileIcon,
  Handshake as MeetAndGreetIcon,
  Autorenew as MakeupIcon,
  PersonAddAlt as ReplacementTeacherIcon,
  EventBusy as CancellationIcon,
  School as ExtraLessonIcon,
  NotificationsActive as AbsenceIcon,
  EditCalendar as RescheduleIcon,
  Celebration as EventIcon,
  BeachAccess as TimeOffIcon,
  AssignmentTurnedIn as AssignmentTurnedInIcon,
  AssignmentLate as AssignmentLateIcon,
} from '@mui/icons-material';
import { formatDistanceToNow } from 'date-fns';

// ===========================================
// TYPES
// ===========================================

export type ActivityType =
  | 'enrollment'
  | 'payment'
  | 'booking'
  | 'booking_rescheduled'
  | 'booking_cancelled'
  | 'attendance'
  | 'file_upload'
  | 'meet_and_greet'
  | 'makeup_eligibility'
  | 'makeup_booking'
  | 'replacement_teacher'
  | 'cancellation_lesson'
  | 'reschedule_lesson'
  | 'extra_lesson_request'
  | 'absence_notification'
  | 'absence_cancelled'
  | 'event_published'
  | 'teacher_time_off'
  | 'permission_slip_signed'
  | 'permission_slip_declined';

export interface ActivityItem {
  id: string;
  type: ActivityType;
  title: string;
  description: string;
  timestamp: string;
  metadata?: Record<string, unknown>;
}

export interface ActivityFeedProps {
  items: ActivityItem[];
  loading?: boolean;
  maxItems?: number;
  onViewAll?: () => void;
  title?: string;
}

// Icon and color mapping for each activity type
const activityConfig: Record<ActivityType, { icon: React.ReactNode; color: string; bgColor: string }> = {
  enrollment: {
    icon: <EnrollmentIcon />,
    color: '#4580E4',
    bgColor: '#a3d9f6',
  },
  payment: {
    icon: <PaymentIcon />,
    color: '#5cb399',
    bgColor: '#c5ebe2',
  },
  booking: {
    icon: <BookingIcon />,
    color: '#E6B800',
    bgColor: '#FFE066',
  },
  booking_rescheduled: {
    icon: <RescheduleIcon />,
    color: '#ed6c02',
    bgColor: '#fff4e5',
  },
  booking_cancelled: {
    icon: <CancellationIcon />,
    color: '#d32f2f',
    bgColor: '#ffcdd2',
  },
  attendance: {
    icon: <AttendanceIcon />,
    color: '#96DAC9',
    bgColor: '#c5ebe2',
  },
  file_upload: {
    icon: <FileIcon />,
    color: '#4580E4',
    bgColor: '#a3d9f6',
  },
  meet_and_greet: {
    icon: <MeetAndGreetIcon />,
    color: '#e67761',
    bgColor: '#ffd4cc',
  },
  makeup_eligibility: {
    icon: <MakeupIcon />,
    color: '#ed6c02',
    bgColor: '#fff4e5',
  },
  makeup_booking: {
    icon: <BookingIcon />,
    color: '#5cb399',
    bgColor: '#c5ebe2',
  },
  replacement_teacher: {
    icon: <ReplacementTeacherIcon />,
    color: '#7c3aed',
    bgColor: '#e9d5ff',
  },
  cancellation_lesson: {
    icon: <CancellationIcon />,
    color: '#d32f2f',
    bgColor: '#ffcdd2',
  },
  // Same icon as the 1:1 clinic reschedule (booking_rescheduled) — both are
  // "this moved" events and should read as one visual language. Brand blue
  // rather than the cancellation red: a reschedule is not a loss.
  reschedule_lesson: {
    icon: <RescheduleIcon />,
    color: '#4580E4',
    bgColor: '#d6e4fa',
  },
  extra_lesson_request: {
    icon: <ExtraLessonIcon />,
    color: '#9B59B6',
    bgColor: '#E8D4F0',
  },
  absence_notification: {
    icon: <AbsenceIcon />,
    color: '#E65100',
    bgColor: '#FFE0B2',
  },
  absence_cancelled: {
    icon: <CancellationIcon />,
    color: '#7B3F00',
    bgColor: '#FFD180',
  },
  teacher_time_off: {
    icon: <TimeOffIcon />,
    color: '#1565c0',
    bgColor: '#a3d9f6',
  },
  event_published: {
    icon: <EventIcon />,
    color: '#4580E4',
    bgColor: '#a3d9f6',
  },
  // Mint for a signature, coral for a decline — both brand accents. A decline
  // is not an ERROR (a family is entitled to say no), so it deliberately does
  // not use the error red.
  permission_slip_signed: {
    icon: <AssignmentTurnedInIcon />,
    color: '#2e7d32',
    bgColor: '#96DAC9',
  },
  permission_slip_declined: {
    icon: <AssignmentLateIcon />,
    color: '#b23c17',
    bgColor: '#FFAE9E',
  },
};

// ===========================================
// COMPONENT
// ===========================================

export function ActivityFeed({
  items,
  loading = false,
  maxItems = 5,
  onViewAll,
  title = 'Recent Activity',
}: ActivityFeedProps) {
  const displayedItems = items.slice(0, maxItems);
  const navigate = useNavigate();
  const { schoolSlug } = useTenant();
  const base = `/${schoolSlug}/admin`;

  const formatRelativeTime = (timestamp: string) => {
    try {
      return formatDistanceToNow(new Date(timestamp), { addSuffix: true });
    } catch {
      return timestamp;
    }
  };

  const getActivityUrl = (item: ActivityItem): string | null => {
    if (!schoolSlug) return null;
    const m = item.metadata ?? {};
    const lessonId = typeof m.lessonId === 'string' ? m.lessonId : null;
    const invoiceId = typeof m.invoiceId === 'string' ? m.invoiceId : null;
    switch (item.type) {
      case 'enrollment':
      case 'booking':
      case 'booking_rescheduled':
      case 'booking_cancelled':
      case 'replacement_teacher':
      case 'cancellation_lesson':
      case 'reschedule_lesson':
        return lessonId ? `${base}/classes/${lessonId}` : null;
      case 'payment':
        return invoiceId ? `${base}/invoices/${invoiceId}` : null;
      case 'meet_and_greet':
        return `${base}/meet-and-greet`;
      case 'extra_lesson_request':
        return `${base}/extra-lessons`;
      case 'absence_notification':
      case 'absence_cancelled':
        return `${base}/absences`;
      case 'teacher_time_off':
        return `${base}/teachers`;
      case 'permission_slip_signed':
      case 'permission_slip_declined':
        return `${base}/permission-slips`;
      case 'makeup_eligibility':
      case 'makeup_booking':
        return `${base}/students`;
      default:
        return null;
    }
  };

  return (
    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <CardHeader
        title={
          <Typography variant="h6" component="div">
            {title}
          </Typography>
        }
        sx={{ pb: 0 }}
      />
      <Divider sx={{ mx: 2, mt: 2 }} />
      <CardContent sx={{ flex: 1, pt: 1, pb: 1, overflow: 'auto' }}>
        {loading ? (
          <List disablePadding>
            {[1, 2, 3, 4, 5].map((i) => (
              <ListItem key={i} sx={{ px: 0 }}>
                <ListItemAvatar>
                  <Skeleton variant="circular" width={40} height={40} />
                </ListItemAvatar>
                <ListItemText
                  primary={<Skeleton variant="text" width="60%" />}
                  secondary={<Skeleton variant="text" width="40%" />}
                />
              </ListItem>
            ))}
          </List>
        ) : displayedItems.length === 0 ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100%',
              minHeight: 150,
            }}
          >
            <Typography color="text.secondary">No recent activity</Typography>
          </Box>
        ) : (
          <List disablePadding>
            {displayedItems.map((item, index) => {
              const config = activityConfig[item.type];
              const url = getActivityUrl(item);
              return (
                <React.Fragment key={item.id}>
                  <ListItem
                    onClick={url ? () => navigate(url) : undefined}
                    sx={{
                      px: 0,
                      py: 1.5,
                      cursor: url ? 'pointer' : 'default',
                      borderRadius: 1,
                      '&:hover': url ? { bgcolor: 'action.hover' } : undefined,
                    }}
                  >
                    <ListItemAvatar>
                      <Avatar
                        sx={{
                          bgcolor: config.bgColor,
                          color: config.color,
                          width: 40,
                          height: 40,
                        }}
                      >
                        {config.icon}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Typography variant="body2" fontWeight={500}>
                          {item.title}
                        </Typography>
                      }
                      secondary={
                        <Box component="span">
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            component="span"
                            sx={{
                              display: 'block',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {item.description}
                          </Typography>
                          {typeof item.metadata?.actorLine === 'string' && (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="span"
                              sx={{ display: 'block', fontStyle: 'italic' }}
                            >
                              {item.metadata.actorLine}
                            </Typography>
                          )}
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            component="span"
                          >
                            {formatRelativeTime(item.timestamp)}
                          </Typography>
                        </Box>
                      }
                    />
                  </ListItem>
                  {index < displayedItems.length - 1 && (
                    <Divider variant="inset" component="li" />
                  )}
                </React.Fragment>
              );
            })}
          </List>
        )}
      </CardContent>
      {onViewAll && items.length > maxItems && (
        <>
          <Divider />
          <Box sx={{ p: 1.5, textAlign: 'center' }}>
            <Button size="small" onClick={onViewAll}>
              View All Activity
            </Button>
          </Box>
        </>
      )}
    </Card>
  );
}

export default ActivityFeed;
